import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleUser,
  faCalculator,
  faCaretDown,
  faSearch,
  faUserSecret,
  faBell,
  faClock,
  faRefresh,
  faTimes
} from '@fortawesome/free-solid-svg-icons'
import NavAdmin, {
  users,
  financial_setups,
  financial_management,
  marketing_tools,
  customer_tools,
  messaging,
  lists,
  developer_tools,
} from './NavAdmin'
import NavLeads, { leads, loans, payments, customers, associated_helpers } from './NavLeads'
import NavLeadFunnel, { lead_funnel } from "./NavLeadFunnel"
import NavProfile from './NavProfile'
import NavCollection, { collections } from './NavCollection'
import { hasUserPermissionOnNavMenuItem, hasUserPermission } from '../../utils/permissionHelper'
import { useRequest } from '../../hooks/UseRequest'
import { setNotification } from '../../redux/notificationSlice'
import { selectUser, setFilteredPortfolio } from '../../redux/userSlice'
import { setModal } from "../../redux/modalSlice"
import { selectPortfolios } from "../../redux/portfolioSlice"
import { selectAuth } from "../../redux/authSlice"
import { fetchUserNotification, selectUserNotification } from "../../redux/userNotificationSlice"
import { selectSimulatedDate, setSimulatedDate, clearSimulatedDate, setNeedsRefresh } from '../../redux/simulatedDateSlice'
import { setPaymentScheduleCurrentDate } from '../../redux/paymentScheduleDataSlice'
import { persistor } from '../../store'
import Loading from '../../components/Loading'

export function NavBarDesktop(props) {
  const { tab, changeTab, collapse, userPermissions } = props
  const [isSearching, setSearching] = useState(null)
  const [portfolioActive, setPortfolioActive] = useState(false)
  const dispatch = useDispatch()
  const loanSearchRef = useRef(null)
  const leadSearchRef = useRef(null)
  const customerSearchRef = useRef(null)
  const paymentSearchRef = useRef(null)
  const quickSearchNonceRef = useRef(0)
  const [disablePortfolio, setDisablePortfolio] = useState(false)
  const request = useRequest()
  const navi = useNavigate()
  const user = useSelector(selectUser)
  const portfolios = useSelector(selectPortfolios)
  const auth = useSelector(selectAuth)
  const userNotification = useSelector(selectUserNotification)
  const simulatedDate = useSelector(selectSimulatedDate)
  const [selectedPortfolio, setSelectedPortfolio] = useState('All Portfolios')
  const [badgeCount, setBadgeCount] = useState(-1)

  const loanFilter = (value) => `{"or":[{"column":"id","value":"${value}","operator":"="},{"column":"guid","value":"${value}","operator":"="}]}`
  const paymentFilter = (value) => `{"or":[{"column":"id","value":"${value}","operator":"="},{"column":"guid","value":"${value}","operator":"="}]}`
  const leadFilter = (value) => `{"or":[{"column":"guid","value":"${value}","operator":"="}]}`

  const customerFilter = (value) => {
    let filter = `{"or":[{"column":"id","value":"${value}","operator":"="},
    {"column":"guid","value":"${value}","operator":"="},
    {"column":"first_name","value":"${value}","operator":"like"},
    {"column":"last_name","value":"${value}","operator":"like"},
    {"column":"ssn_last_four","value":"${value}","operator":"="},
    {"column":"ssn_encrypted","value":"${value}","operator":"="},
    {"column":"email_address","value":"${value}","operator":"="},
    {"column":"home_phone_number","value":"${value}","operator":"="},
    {"column":"cell_phone_number","value":"${value}","operator":"="}`

    // for birthdate check if it is a date format '02/29/2020'
    const dateReg = /^\d{2}[./-]\d{2}[./-]\d{4}$/
    if (dateReg.test(value)) {
      // swap the format to match db '2020/02/29'
      const fixedDate = `${value.substring(6, 10)}-${value.substring(0, 2)}-${value.substring(3, 5)}`
      filter += `,{"column":"birth_date","value":"${fixedDate}","operator":"="}`
    }

    if (value.indexOf('-') !== -1 && value.search("-") == 3) {
      //let's get ride of hyphens format for the phone numbers eg: 111-111-1111 into 1111111111
      value = value.replace(/-/g, "");
      filter += `,{"column":"home_phone_number","value":"${value}","operator":"like"},
      {"column":"cell_phone_number","value":"${value}","operator":"like"}]}`
    } else {
      filter += `]}`
    }
    return filter;
  }

  /**
   * Restrict the Portfolio Dropdown to just one option if limit to portfolio is set
   * Populate the initial value if Redux holds a filtered portfolio value
   */
  useEffect(() => {
    if (user.data && portfolios.data.length > 0) {
      if (user.data.limit_to_portfolio_id || user?.spoofed_user?.limit_to_portfolio_id) {
        let limitedPortfolioIds = user?.spoofed_user?.limit_to_portfolio_id ?? (user.data.limit_to_portfolio_id);
        let limitedPortfolio = []
        limitedPortfolioIds.forEach((portfolioId) => {
          limitedPortfolio.push(portfolios.data.filter((portfolio) => portfolio.id === portfolioId))
        })
        if (limitedPortfolio.length === 1) {
          let limitedPortfolioObject = limitedPortfolio[0][0];
          setSelectedPortfolio(limitedPortfolioObject.name)
          dispatch(setFilteredPortfolio({ filtered_portfolio: limitedPortfolioObject.id }))
          setDisablePortfolio(true)
        }
      } else if (user.filtered_portfolio) {
        setDisablePortfolio(false)
        const nameFiltered = portfolios.data.find((portfolio) => portfolio.id === user.filtered_portfolio).name
        setSelectedPortfolio(nameFiltered)
      }
    }
  }, [user, portfolios])

  const getQuickSearch = async ({ searchPayloadType }) => {
    if (isSearching) return
    let searchType = searchPayloadType
    if (!searchType) {
      if (loanSearchRef.current.value) {
        searchType = 'loan'
      } else if (leadSearchRef.current.value) {
        searchType = 'lead'
      } else if (customerSearchRef.current.value) {
        searchType = 'customer'
      } else {
        searchType = 'payment'
      }
    }

    let value;

    switch (searchType) {
      case 'loan':
        value = loanSearchRef.current.value;
        break;
      case 'lead':
        value = leadSearchRef.current.value;
        break;
      case 'customer':
        value = customerSearchRef.current.value;
        break;
      case 'payment':
        value = paymentSearchRef.current.value;
    }

    if (value) {
      setSearching(true)
      let urlFilter;
      switch (searchType) {
        case 'loan':
          urlFilter = `?filters=${loanFilter(value)}`;
          break;
        case 'lead':
          urlFilter = `?filters=${leadFilter(value)}`;
          break;
        case 'customer':
          urlFilter = `?filters=${customerFilter(value)}`;
          break;
        case 'payment':
          urlFilter = `?filters=${paymentFilter(value)}`;
          break;
      }

      try {
        let response;
        if (searchType === 'customer') {
          // This search can match on SSN, so it uses POST instead of GET to keep the value
          // out of the URL. useRequest's post() doesn't append spoofUserID like get() does,
          // so it's added manually here: the backend reads it from the query string, not
          // the JSON body.
          const spoofParam = user.spoofed_user?.id ? `?spoofUserID=${user.spoofed_user.id}` : ''
          request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/search${spoofParam}`)
          response = await request.post({ filters: customerFilter(value) })
        } else {
          const searchQuery = `${searchType}${urlFilter}`
          request.setEndpoint(
            `${process.env.REACT_APP_API_DOMAIN}/${searchQuery}`
          )
          response = await request.get()
        }
        const responseData = response.data.data

        if (!responseData.length) {
          dispatch(setNotification({ type: 'warning', body: 'No search results' }))
        } else if (responseData.length === 1) {
          if (searchType === "loan") {
            navi(`/payment_schedule/${responseData[0].customer_id}?loan=${responseData[0].guid}`)
          } else if (searchType === "lead") {
            navi(`/lead/${responseData[0].id}`)
          } else if (searchType === "payment") {
            request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan?filters=${loanFilter(responseData[0].loan_id)}`)
            const loanResponse = await request.get();
            if (loanResponse.data.data.length > 0) {
              navi(`/payment_schedule/${loanResponse.data.data[0].customer_id}?loan=${loanResponse.data.data[0].guid}`)
            } else {
              dispatch(setNotification({ type: 'warning', body: 'No search results' }))
            }
          } else {
            navi(`/${searchType}/${responseData[0].id}`)
          }
        } else {
          // ModuleList.jsx re-reads location.state only when location.search changes, so the
          // marker must vary per search even though its value is never parsed back out.
          navi(`/${searchType}/?quick_search=${searchType}-${quickSearchNonceRef.current++}`, { state: { quickSearchData: response.data, urlFilter: urlFilter } })
        }
        clearSearchValue()
      } catch (error) {
        dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
      }
      setSearching(false)
    }
  }

  const handleKeyDown = (event) => {
    if (event?.key === 'Enter') getQuickSearch({ searchPayloadType: event.target.dataset.searchPayload })
  }

  const clearSearchValue = () => {
    loanSearchRef.current.value = ''
    customerSearchRef.current.value = ''
    leadSearchRef.current.value = ''
    paymentSearchRef.current.value = ''
  }

  const togglePortfolio = (event) => {
    if (event.type === 'mouseLeave') {
      setPortfolioActive(false)
      changeTab('none')
      return;
    }

    if (disablePortfolio) return;

    if (portfolioActive) {
      changeTab('none')
      setPortfolioActive(false)
    } else {
      changeTab('portfolio')
      setPortfolioActive(true)
    }
  }

  const changePortfolio = (name, id) => {
    setSelectedPortfolio(name)
    collapse()
    dispatch(setFilteredPortfolio({ filtered_portfolio: id }))
    setPortfolioActive(false)
  }

  const clickOffNav = (event) => {
    if (event.target.className === 'navbar-menu' || event.target.className === "lox-navbar-menu-container") {
      collapse()
    }
  }

  const getNotifications = () => {
    let endpoint = `user/${user.data.id}/notification`
    if (userNotification.meta && Object.keys(userNotification.meta).length === 0 && userNotification.meta.current_page) {
      endpoint += `?page=${userNotification.meta.current_page}`
    }

    dispatch(
      fetchUserNotification({
        accessToken: auth.accessToken,
        endpoint: endpoint,
        spoofedUserId: user.spoofed_user.id ? user.spoofed_user.id : null
      })
    ).then(({ error }) => {
      if (error) {
        if (error.message.includes('status code 401')) {
          dispatch(setNotification({ type: 'error', body: error.message }))
        } else if (error.message.includes('status code 404')) {
          dispatch(setNotification({ type: 'error', body: error.message }))
        } else {
          dispatch(setNotification({ type: 'error', body: error.message }))
        }
      }
    })
  }

  /**
   * Notification Polling for auto updates
   */
  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken && Object.keys(user.data).length > 0 && userNotification.status === 'succeeded') {
      const interval = setInterval(() => {
        getNotifications();
      }, 30000);
      return () => clearInterval(interval)
    }
  }, [auth.accessToken, user, userNotification])

  /**
   * Get User Notification Data for the bell icon
   */
  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken && Object.keys(user.data).length > 0) {
      getNotifications()
    }
  }, [auth, user])

  useEffect(() => {
    if (userNotification.status === 'succeeded') {
      setBadgeCount(userNotification.extra.total_not_viewed)
    }
  }, [userNotification])

  return (
    <div
      className={tab === 'burger' ? 'navbar-menu is-active' : 'navbar-menu'}
      id="nav-links"
      onClick={clickOffNav}
    >
      <div className="columns is-flex-direction-column is-align-items-start m-0 lox-navbar-menu-container">
        <span className="column is-size-6 p-0 pt-1 is-flex">
          <div className="navbar-item p-0 px-1">
            <div
              className={
                tab === 'portfolio' ? 'dropdown is-active' : 'is-active'
              }
            >
              <div className="dropdown-trigger">
                <button
                  disabled={disablePortfolio}
                  className="button lox-is-rounded is-small"
                  onClick={togglePortfolio}
                >
                  <span>{selectedPortfolio}</span>
                  {!disablePortfolio && (
                    <span className="icon is-small">
                      <span className="pl-1">
                        <FontAwesomeIcon icon={faCaretDown} />
                      </span>
                    </span>
                  )}
                </button>
              </div>
              {tab === 'portfolio' && (
                <div className="dropdown-menu" role="menu">
                  <div className="dropdown-content">
                    {portfolios.data.map((portfolio) => (
                      <a
                        className="dropdown-item py-1 is-clickable"
                        onClick={() => changePortfolio(portfolio.name, portfolio.id)}
                        key={portfolio.id}
                      >
                        {portfolio.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search Customer or Search Lead Guid or Search Loan ID */}
          <div className="navbar-item  p-0 px-1">
            <input
              ref={loanSearchRef}
              className="input lox-is-rounded is-small mr-1 input-width-100px"
              type="text"
              data-search-payload="loan"
              placeholder="Loan #"
              onKeyDown={handleKeyDown}
            />
            <input
              ref={leadSearchRef}
              className="input lox-is-rounded is-small mr-1 input-width-100px"
              type="text"
              data-search-payload="lead"
              placeholder="Lead GUID"
              onKeyDown={handleKeyDown}
            />
            <input
              ref={customerSearchRef}
              className="input lox-is-rounded is-small mr-1 input-width-100px"
              type="text"
              data-search-payload="customer"
              placeholder="Customer"
              onKeyDown={handleKeyDown}
            />
            <input
              ref={paymentSearchRef}
              className="input lox-is-rounded is-small input-width-100px"
              type="text"
              data-search-payload="payment"
              placeholder="Payment ID"
              onKeyDown={handleKeyDown}
            />
            {!isSearching ? (
              <button className="button is-ghost lox-ghost px-1">
                <span
                  className="icon is-size-5 p-0 has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-secondary lox-no-border"
                  onClick={getQuickSearch}
                  data-tooltip={"Search using the following:\nLoan: ID, GUID\nLead: GUID\nCustomer: ID, GUID, SSN (w/ or without dashes), Name (First or Last), last 4 SSN digits, DOB (MM/DD/YYYY), phone number, and email\n Payment: ID, GUID"}
                >
              <FontAwesomeIcon
                icon={faSearch}
                className={process.env.REACT_APP_ENVIRONMENT !== 'production' ? 'dev-env-color' : ''}
              />
                </span>
              </button>
            ) : (
              <Loading
                colorClass={'has-text-light'}
                size={"is-size-6"}
              />
            )}
          </div>

          {/* Inline System Clock (non-production only) */}
          {process.env.REACT_APP_ENVIRONMENT !== 'production' && (
            <div className="navbar-item p-0 px-1 is-flex is-align-items-center">
              <FontAwesomeIcon icon={faClock} className="dev-env-color mr-1" />
              <input
                type="datetime-local"
                className="input is-small"
                style={{ width: '210px' }}
                value={simulatedDate.simulated_date ? simulatedDate.simulated_date.replace(' ', 'T') : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const date = new Date(e.target.value)
                    const formatted = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
                    dispatch(setSimulatedDate({ simulated_date: formatted }))
                    dispatch(setPaymentScheduleCurrentDate(formatted))
                    dispatch(setNeedsRefresh(true))
                  }
                }}
                step="1"
              />
              {simulatedDate.simulated_date && (
                <button
                  className="button is-small is-danger is-outlined mx-1 px-2"
                  title="Clear spoof date"
                  onClick={async () => {
                    dispatch(clearSimulatedDate())
                    dispatch(setPaymentScheduleCurrentDate(null))
                    await persistor.flush()
                    window.location.reload()
                  }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
              {simulatedDate.needsRefresh && (
                <button
                  className="button is-small is-link mx-1 px-2 has-tooltip-arrow has-tooltip-bottom has-tooltip-primary"
                  data-tooltip="Spoof date changed - refresh page"
                  onClick={() => window.location.reload()}
                >
                  <FontAwesomeIcon icon={faRefresh} />
                </button>
              )}
            </div>
          )}
        </span>
        <span className="navbar-start column is-flex is-flex-direction-row p-0">
          {hasUserPermissionOnNavMenuItem(userPermissions, leads, loans, payments, customers, associated_helpers) && (
            <span>
              <div className="navbar-item has-dropdown ">
                <span
                  className={
                    tab === 'leads'
                      ? 'navbar-link is-arrowless px-2 py-1 nav-active'
                      : 'navbar-link is-arrowless px-2 py-1'
                  }
                  onClick={() => changeTab('leads')}
                >
                  Core
                  <span className="pl-1">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </span>
                </span>
              </div>
              {tab === 'leads' && (
                <NavLeads onLink={collapse} userPermissions={userPermissions} />
              )}
            </span>
          )}

          {hasUserPermissionOnNavMenuItem(userPermissions, lead_funnel) && (
            <span>
              <div className="navbar-item has-dropdown ">
                <span
                  className={
                    tab === 'lead_funnel'
                      ? 'navbar-link is-arrowless px-2 py-1 nav-active'
                      : 'navbar-link is-arrowless px-2 py-1'
                  }
                  onClick={() => changeTab('lead_funnel')}
                >
                  Lead Funnel
                  <span className="pl-1">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </span>
                </span>
              </div>
              {tab === 'lead_funnel' && (
                <NavLeadFunnel onLink={collapse} userPermissions={userPermissions} />
              )}
            </span>
          )}

          {hasUserPermissionOnNavMenuItem(userPermissions, collections) && (
            <span>
              <div className="navbar-item has-dropdown ">
                <span
                  className={`navbar-link is-arrowless px-2 py-1 ${tab === 'collection' && 'nav-active'
                    }`}
                  onClick={() => changeTab('collection')}
                >
                  Collections
                  <span className="pl-1">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </span>
                </span>
              </div>
              {tab === 'collection' && <NavCollection onLink={collapse} userPermissions={userPermissions} />}
            </span>
          )}

          {hasUserPermission(userPermissions, 'queue_report_file_read') && (
            <span>
              <div className="navbar-item has-dropdown ">
                <span
                  className={`navbar-link is-arrowless px-2 py-1 ${tab === 'report' && 'nav-active'
                    }`}
                  onClick={() => {
                    changeTab('report')
                    navi('/report')
                  }}
                >
                  Reports
                </span>
              </div>
            </span>
          )}

          {hasUserPermission(userPermissions, 'queue_tribal_report_file_read') && (
            <span>
              <div className="navbar-item has-dropdown ">
                <span
                  className={`navbar-link is-arrowless px-2 py-1 ${tab === 'tribal_report' && 'nav-active'
                  }`}
                  onClick={() => {
                    changeTab('tribal_report')
                    navi('/tribal_report')
                  }}
                >
                  Tribal Reports
                </span>
              </div>
            </span>
          )}

          {hasUserPermissionOnNavMenuItem(
            userPermissions,
            users,
            financial_setups,
            financial_management,
            marketing_tools,
            customer_tools,
            messaging,
            lists,
            developer_tools
          ) && (
              <span>
                <div className="navbar-item has-dropdown">
                  <span
                    className={`navbar-link is-arrowless px-2 py-1 ${tab === 'admin' && 'nav-active'
                      }`}
                    onClick={() => changeTab('admin')}
                  >
                    Admin
                    <span className="pl-1">
                      <FontAwesomeIcon icon={faCaretDown} />
                    </span>
                  </span>
                </div>
                {tab === 'admin' && <NavAdmin onLink={collapse} userPermissions={userPermissions} />}
              </span>
            )}
        </span>
      </div>

      {/* Navigation Bar Right Side */}
      <div className={`navbar-end ${process.env.REACT_APP_ENVIRONMENT !== 'production' ? 'has-background-primary' : ''}pr-3`}>
        <div className="columns is-flex-direction-column m-0 flex-column-center">
          <span className="is-flex">
            <h6 className={`subtitle is-primary has-text-centered is-size-6 p-0 lox-navigation-bar__content__env-title ${process.env.REACT_APP_ENVIRONMENT !== 'production' ? 'dev-env-color-db' : ''} ${process.env.REACT_APP_ENVIRONMENT === 'production' && 'is-hidden'}`}>
              Environment:
              <b> {process.env.REACT_APP_ENVIRONMENT} </b>
            </h6>
          </span>
          <span className="is-flex is-align-self-flex-end">
            <div className="navbar-item pr-1">
              <div className="buttons mx-0 px-0">
                {user.spoofed_user.id ? (
                  <button className="button is-ghost lox-ghost mx-0 px-0">
                    <span className="icon">
                      <FontAwesomeIcon
                        icon={faUserSecret}
                        style={{ color: "yellow", cursor: "default" }}
                        className={process.env.REACT_APP_ENVIRONMENT !== 'production' ? 'dev-env-color' : ''}
                      />
                    </span>
                  </button>
                ) : ''}
                <button className="button is-ghost lox-ghost mx-0 px-0" onClick={() => dispatch(setModal({ title: `Loan Calculator`, content: ``, type: 'calculator', is_active: true }))}>
                  <span className="icon">
                    <FontAwesomeIcon
                      icon={faCalculator}
                      className={process.env.REACT_APP_ENVIRONMENT !== 'production' ? 'dev-env-color' : ''}
                    />
                  </span>
                </button>
              </div>
            </div>
            <div className="navbar-item p-0">
              <div className={tab === 'profile' ? 'dropdown is-right is-active' : 'is-active'}>
                <div className="dropdown-trigger">
                  <button
                    className="button is-small lox-ghost is-ghost is-size-6 p-0"
                    onClick={() => changeTab('profile')}
                  >
                    <span className="icon">
                      <FontAwesomeIcon
                        icon={faCircleUser}
                        className={process.env.REACT_APP_ENVIRONMENT !== 'production' ? 'dev-env-color' : ''}
                      />
                    </span>
                  </button>
                </div>
                {tab === 'profile' && <NavProfile onLink={collapse} />}
              </div>
            </div>
            <div className="navbar-item p-0">
              <Link
                to={'/user/notification'}
                title="notifications"
                className="is-flex is-justify-content-space-between is-align-items-center"
              >
                <FontAwesomeIcon icon={faBell} className={`${process.env.REACT_APP_ENVIRONMENT !== 'production' ? 'dev-env-color' : ''} has-text-white is-size-5 is-clickable is-divider mx-2`} />
                {badgeCount > 0 && (
                  <span className="tag is-danger ml-1 lox-tag">
                    {badgeCount}
                  </span>
                )}
              </Link>
            </div>
          </span>
        </div>
      </div>
      {/* End of Navigation Bar Right Side */}
    </div>
  )
}
